import React, { useState, useEffect } from 'react';
import clairityIntegratedLogo from './assets/clairity-integrated-logo.png';
import { supabase } from './utils/supabase';
import ReviewsTable from './components/ReviewsTable';
import FilterPanel from './components/FilterPanel';
import SummaryStats from './components/SummaryStats';
import AIInsights from './components/AIInsights';
import AIChat from './components/AIChat';
import AISummary from './components/AISummary';
import AITrends from './components/AITrends';
import PeakPerformers from './components/PeakPerformers';
import TutorialModal from './components/TutorialModal';
import { BarChart3, Bot, MessageCircle, FileText, TrendingUp, Trophy, HelpCircle } from 'lucide-react';
import './styles/App.css';

function App() {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showTutorial, setShowTutorial] = useState(false);
  const [filters, setFilters] = useState({
    segment: '',
    rating: '',
    dateFrom: '',
    dateTo: '',
    searchText: '',
    datePreset: 'all'
  });

  useEffect(() => {
    fetchAllReviews();
    
    // Show tutorial on first visit
    const hasSeenTutorial = localStorage.getItem('clairity-tutorial-seen');
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reviews, filters]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchAllReviews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('Camelback Reviews')
        .select('*')
        .order('DATE', { ascending: false });

      if (error) throw error;
      
      setReviews(data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reviews];

    if (filters.segment) {
      filtered = filtered.filter(review => review.SEGMENT === filters.segment);
    }

    if (filters.rating) {
      filtered = filtered.filter(review => review.RATING === parseInt(filters.rating));
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(review => new Date(review.DATE) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter(review => new Date(review.DATE) <= new Date(filters.dateTo));
    }

    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(review => 
        review.REVIEW?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredReviews(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleTutorialClose = () => {
    setShowTutorial(false);
    localStorage.setItem('clairity-tutorial-seen', 'true');
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Gaining Clairity...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            <SummaryStats reviews={filteredReviews} allReviews={reviews} />
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <ReviewsTable reviews={filteredReviews} />
          </>
        );
      case 'ai-insights':
        return (
          <>
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <AIInsights reviews={filteredReviews} />
          </>
        );
      case 'ai-chat':
        return (
          <>
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <AIChat reviews={filteredReviews} />
          </>
        );
      case 'ai-summary':
        return (
          <>
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <AISummary 
              reviews={reviews} 
              filteredReviews={filteredReviews}
              currentFilters={filters}
            />
          </>
        );
      case 'ai-trends':
        return (
          <>
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <AITrends reviews={filteredReviews} />
          </>
        );
      case 'peak-performers':
        return (
          <>
            <PeakPerformers reviews={reviews} />
          </>
        );
      default:
        return (
          <>
            <SummaryStats reviews={filteredReviews} allReviews={reviews} />
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <ReviewsTable reviews={filteredReviews} />
          </>
        );
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-brand">
          <img src={clairityIntegratedLogo} alt="Clairity - Powered by Aumen Labs" className="integrated-logo" />
        </div>
        <div className="header-property">
          <span className="property-label">Property:</span>
          <span className="property-name">Camelback Resort</span>
        </div>
      </header>
      
      <nav className="app-nav">
        <div className="nav-container">
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <BarChart3 size={16} />
              Dashboard
            </button>
            <button 
              className={`nav-tab ${activeTab === 'ai-insights' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai-insights')}
            >
              <Bot size={16} />
              AI Insights
            </button>
            <button 
              className={`nav-tab ${activeTab === 'ai-chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai-chat')}
            >
              <MessageCircle size={16} />
              AI Chat
            </button>
            <button 
              className={`nav-tab ${activeTab === 'ai-summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai-summary')}
            >
              <FileText size={16} />
              AI Summary
            </button>
            <button 
              className={`nav-tab ${activeTab === 'ai-trends' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai-trends')}
            >
              <TrendingUp size={16} />
              Trends
            </button>
            <button 
              className={`nav-tab ${activeTab === 'peak-performers' ? 'active' : ''}`}
              onClick={() => setActiveTab('peak-performers')}
            >
              <Trophy size={16} />
              Peak Performers
            </button>
          </div>
          <button className="nav-help-btn" onClick={() => setShowTutorial(true)}>
            <HelpCircle size={16} />
            <span>Tutorial</span>
          </button>
        </div>
      </nav>
      
      <main className="app-main">
        {renderTabContent()}
      </main>

      <TutorialModal 
        isOpen={showTutorial} 
        onClose={handleTutorialClose} 
      />
    </div>
  );
}

export default App;